import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from 'semantic-ui-react';
import moment from 'moment';
import { getBirthdaySignedImageUrls, listAllBirthdays } from '../../services/Birthdays';
import { Birthday } from '../../types/Birthday';
import TruncatedText from '../TruncatedText';
import sendToast from '../../../utils/Toast';

interface Props {
    facilityId: string;
    groupResidentIds?: string[];
    onProfileClick?: (residentId: string) => void;
}

// Caps how many getBirthdaySignedImageUrls calls run at once, so a facility
// with many same-day birthdays doesn't fire an unbounded burst of Lambda calls.
const MAX_CONCURRENT_SIGNED_URL_FETCHES = 6;
const PLACEHOLDER_IMAGE = '/avatar_placeholder.png';

// live-be sends displayImageUrl (the resident's Neighbors photo, else PCC, else staff photo) on Neighbors calls.
// Only a full http(s) link can be shown as is; anything else goes through the signed-URL path like before.
const getUsableDisplayImage = (birthday: Birthday): string => {
    const url = (birthday.displayImageUrl || '').trim();
    return /^https?:\/\//i.test(url) ? url : '';
};

const BirthdaysSection = ({ facilityId, groupResidentIds, onProfileClick }: Props) => {
    const [birthdays, setBirthdays] = useState<Birthday[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    // birthday.image is just the S3 filename in a private bucket, not a usable URL,
    // so each one needs its own signed URL before it can be rendered.
    const [imageUrlById, setImageUrlById] = useState<Record<string, string>>({});
    // Ids we've already kicked off a signed-URL fetch for (resolved or failed), so
    // a group-filter change doesn't refetch rows already attempted.
    const attemptedImageIdsRef = useRef<Set<string>>(new Set());
    // Photo links that failed to load, so the row moves on to the next one (signed URL, then placeholder).
    const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(new Set());
    const isMountedRef = useRef(true);
    useEffect(() => () => { isMountedRef.current = false; }, []);

    useEffect(() => {
        let cancelled = false;
        const fetchBirthdays = async () => {
            try {
                setIsLoading(true);
                const today = moment().format('YYYY-MM-DD');
                const response = await listAllBirthdays({
                    facilityId,
                    startDate: today,
                    endDate: today,
                });
                const result = response.Result || [];
                if (cancelled) return;
                setBirthdays(result);
                setImageUrlById({});
                setFailedImageUrls(new Set());
                attemptedImageIdsRef.current = new Set();
            } catch (error) {
                if (!cancelled) sendToast('error', error instanceof Error ? error.message : 'Error fetching birthdays');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        fetchBirthdays();
        return () => { cancelled = true; };
    }, [facilityId]);

    const filteredBirthdays = useMemo(() => (
        groupResidentIds?.length
            ? birthdays.filter(b => b.residentId && groupResidentIds.includes(String(b.residentId)))
            : birthdays
    ), [birthdays, groupResidentIds]);

    // Only sign URLs for rows the group filter actually leaves visible, and only
    // once per row - a later group-filter change can still reveal new rows that
    // haven't been attempted yet, without re-requesting ones already resolved.
    useEffect(() => {
        let cancelled = false;
        // A row with a loadable displayImageUrl already has its photo, so it needs no signed URL.
        const needsSignedUrl = (b: Birthday) => {
            const displayImage = getUsableDisplayImage(b);
            return !displayImage || failedImageUrls.has(displayImage);
        };
        const pending = filteredBirthdays.filter(b => b.image && needsSignedUrl(b) && !attemptedImageIdsRef.current.has(b._id));
        if (!pending.length) return undefined;

        let nextIndex = 0;
        const runNext = async (): Promise<void> => {
            if (cancelled) return;
            const current = nextIndex++;
            if (current >= pending.length) return;
            const b = pending[current];
            // Marked only once this row's fetch actually starts, not for the whole
            // batch upfront — a row that never gets a worker turn before the effect
            // is cancelled (e.g. group filter changes again) stays eligible for retry.
            attemptedImageIdsRef.current.add(b._id);
            try {
                const signedUrl = await getBirthdaySignedImageUrls({ facilityId, birthdayId: b._id, fileName: b.image as string });
                // Kept even if this run was superseded (filter change, a photo failing): the row is already
                // marked attempted, so the next run would not fetch it again.
                if (isMountedRef.current) setImageUrlById(prev => ({ ...prev, [b._id]: signedUrl }));
            } catch {
                // Avatar falls back to the placeholder; not worth a toast per row.
            }
            return runNext();
        };

        const workerCount = Math.min(MAX_CONCURRENT_SIGNED_URL_FETCHES, pending.length);
        void Promise.all(Array.from({ length: workerCount }, runNext));

        return () => { cancelled = true; };
    }, [filteredBirthdays, facilityId, failedImageUrls]);

    const getBirthdayImage = (birthday: Birthday): string => (
        [getUsableDisplayImage(birthday), imageUrlById[birthday._id]].find(url => url && !failedImageUrls.has(url))
        || PLACEHOLDER_IMAGE
    );

    const handleImageError = (failedUrl: string) => {
        if (failedUrl === PLACEHOLDER_IMAGE) return;
        setFailedImageUrls(prev => (prev.has(failedUrl) ? prev : new Set(prev).add(failedUrl)));
    };

    return (
        <div className="birthdays-section">
            <div className="section-header">
                <Icon name="birthday cake" />
                <span>Birthdays Today</span>
            </div>
            <div className="birthdays-list">
                {isLoading ? (
                    <p className="birthdays-loading">Loading...</p>
                ) : filteredBirthdays.length === 0 ? (
                    <p className="birthdays-empty">No Birthdays today</p>
                ) : (
                    filteredBirthdays.map(birthday => {
                        const isClickable = Boolean(birthday.residentId && onProfileClick);
                        const imageSrc = getBirthdayImage(birthday);
                        return (
                            <div
                                key={birthday._id}
                                className="birthday-entry"
                                role={isClickable ? 'button' : undefined}
                                tabIndex={isClickable ? 0 : undefined}
                                onClick={() => birthday.residentId && onProfileClick?.(birthday.residentId)}
                                onKeyDown={(e) => {
                                    if (!isClickable || !birthday.residentId) return;
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onProfileClick?.(birthday.residentId);
                                    }
                                }}
                                style={{ cursor: isClickable ? 'pointer' : undefined }}
                            >
                                <img
                                    src={imageSrc}
                                    alt={birthday.name}
                                    className="birthday-avatar"
                                    onError={() => handleImageError(imageSrc)}
                                />
                                <div className="birthday-info">
                                    <TruncatedText
                                        className="birthday-name"
                                        forcePopup
                                        position="top center"
                                        popperModifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
                                    >
                                        {birthday.name}
                                    </TruncatedText>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default BirthdaysSection;
